/* ══════════════════════════════════════════════════════════════════
   NDS MOBILE DRAWER — Controller (production-ready)
   ══════════════════════════════════════════════════════════════════
   Responsibilities
   ───────────────
   • Toggle drawer open / closed from any [data-nds-drawer-toggle]
     (the site burger) and any [data-nds-drawer-close].
   • Route between "main" and "sub" views via sliding rail.
   • Scroll-lock body while open (CSS-class-only — we never move
     window.scrollY, mirroring the collection modal's approach).
   • Focus-trap inside the drawer when open; restore focus on close.
   • Dispatch nds:modal:open / nds:modal:close so the sticky header
     treats the drawer as a first-class modal (stays visible, not
     hidden by scroll-up behavior).
   • Keep .nds-cm (collection modal) delegation intact: cards inside
     the drawer with [data-nds-cm-open] still open the right modal.
     We auto-close the drawer when that happens so the modal has
     a clean backdrop.
   • Currency sheet: simple toggle + close after currency pick.
   • Robust to shopify:section:load (section re-render) — module is
     self-contained and re-binds on IIFE execution.
   ══════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  // ─── DOM refs (queried fresh on init so shopify:section:load re-runs cleanly).
  //     No backdrop / btnClose anymore — the drawer sits under the site
  //     header at z:1 within the header-wrapper stacking context (same
  //     trick as .nds-cm collection modal), so the site burger IS the
  //     close button; the drawer has no chrome of its own.
  var drawer, rail, btnBack, btnSheetToggle,
      sheet, sheetBackdrop, sheetClose;

  var state = {
    open: false,
    view: 'main',   // 'main' | 'sub'
    activeCat: null, // handle string
    lastFocus: null,
    railHandoff: false
  };
  var globalsWired = !!window.__ndsMobileDrawerGlobalsWired;

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  // ──────────────────────────────────────────────────────────────
  //  Motion One engine — Apple-style spring orchestration
  //  ──────────────────────────────────────────────────────────
  //  The drawer falls back to CSS transitions (see the base rules
  //  in nds-mobile-drawer.css) whenever Motion One is unavailable
  //  OR the user has `prefers-reduced-motion: reduce`. With Motion
  //  available we add `.nds-motion-on` to the drawer root; that
  //  class neutralises the CSS transitions we're about to own
  //  from JS and lets WAAPI do the heavy lifting.
  // ──────────────────────────────────────────────────────────────
  var M = (typeof window !== 'undefined' && window.Motion) ? window.Motion : null;
  var prefersReduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var animEnabled = !!M && !prefersReduced;

  // Spring profiles tuned to feel Apple-ish: firm settle with minimal
  // overshoot. The drawer animates only screen-level surfaces (shell,
  // rail, sheet). Individual catalog buttons/cards stay static inside
  // their moving view to avoid layout flicker on mobile GPUs.
  var SPRING_SHELL = { stiffness: 260, damping: 30, mass: 1 };
  var SPRING_RAIL  = { stiffness: 300, damping: 34, mass: 1 };
  var SPRING_SHEET = { stiffness: 320, damping: 32, mass: 1 };

  // Fast exits: close / reverse-rail don't need a settling spring,
  // a short ease-in is snappier and lines up with Apple's iOS
  // dismiss feel (content exits faster than it arrives).
  var EASE_EXIT = [0.32, 0, 0.67, 0];

  // Track the currently-running animation for each logical channel
  // so interruptions (user taps burger mid-open, taps back while
  // rail is still sliding, …) stop the old one instead of stacking.
  var anims = { shell: null, rail: null, sheet: null, backdrop: null };

  function stopAnim(key) {
    var a = anims[key];
    if (!a) return;
    try { if (typeof a.stop === 'function') a.stop(); } catch (e) {}
    anims[key] = null;
  }
  function stopAllAnims() {
    Object.keys(anims).forEach(stopAnim);
  }

  function init() {
    drawer        = $('[data-nds-drawer]');
    if (!drawer) return;                      // section not on page
    rail          = $('[data-nds-drawer-rail]', drawer);
    btnBack       = $all('[data-nds-drawer-back]', drawer);
    sheet         = $('[data-nds-drawer-sheet]', drawer);
    sheetBackdrop = $('[data-nds-drawer-sheet-backdrop]', drawer);
    sheetClose    = $('[data-nds-drawer-sheet-close]', drawer);

    // Activate the motion-on CSS hand-off (see nds-mobile-drawer.css
    // section 9) when Motion One is available AND the user hasn't
    // asked for reduced motion. Removing it here on re-init keeps
    // Shopify's `shopify:section:load` (theme editor live-reload)
    // from doubling up the class.
    if (animEnabled) drawer.classList.add('nds-motion-on');
    else             drawer.classList.remove('nds-motion-on');

    if (!drawer.hasAttribute('data-nds-drawer-wired')) {
      bindCategoryOpens();
      bindBackButtons();
      bindAccordions();
      bindSheet();
      drawer.setAttribute('data-nds-drawer-wired', 'true');
    }

    if (!globalsWired) {
      bindTriggers();
      bindGlobalKeys();
      bindModalTriggers();
      bindFocusTrap();
      bindIosScrollGuard();
      globalsWired = true;
      window.__ndsMobileDrawerGlobalsWired = true;
    }
  }

  // ──────────────────────────────────────────────────────────────
  //  Open/close core
  // ──────────────────────────────────────────────────────────────
  function open() {
    if (!drawer || state.open) return;
    state.open = true;
    state.lastFocus = document.activeElement;

    // Detect mid-close interruption — a running shell anim means
    // the drawer is currently fading OUT and the user just asked
    // to re-open. Motion's stop() (called below inside
    // stopAllAnims) will commitStyles() the current interpolated
    // opacity/transform to inline so the reverse spring can pick
    // them up as its starting frame.
    var reversingClose = !!anims.shell;

    // Cancel any in-flight close animation so rapid burger taps
    // re-open from the current (partially-closed) visual state
    // instead of stacking two animations on the same element.
    stopAllAnims();

    // When Motion is active, prime the initial visual state as
    // inline styles BEFORE flipping data-open="true". That way
    // the CSS [data-open="true"] rule (which would otherwise pop
    // opacity to 1 instantly because motion-on kills the CSS
    // transition) can't fight Motion's first frame. Skip this
    // seed on a close-reversal — the committed inline values from
    // stopAllAnims() are the correct starting frame and wiping
    // them would produce a visible flash down to invisible before
    // the spring climbs back.
    if (animEnabled && !reversingClose) {
      drawer.style.opacity = '0';
      drawer.style.transform = 'translateY(10px) scale(0.985)';
    }

    drawer.setAttribute('data-open', 'true');
    drawer.setAttribute('aria-hidden', 'false');

    var burger = $('[data-burger-toggle]');
    if (burger) burger.setAttribute('aria-expanded', 'true');

    // Body scroll-lock — identical technique to the desktop collection
    // modal. The `nds-drawer-open` class carries
    //     overflow: hidden; touch-action: none; overscroll-behavior: none
    // which is enough to freeze the page on every modern browser
    // including iOS Safari 15+. We intentionally DO NOT set
    // position:fixed + top:-scrollY on <body>: that trick resets
    // window.scrollY to 0, which silently poisons the site header's
    // scroll-up `prevY` cache. On close, the cached prevY=0 would
    // then make the next scroll event (from us restoring scroll)
    // look like a downward scroll of +savedY pixels and hide the
    // header. By leaving window.scrollY intact, no scroll restore
    // is ever needed and none of those edge cases can happen.
    document.body.classList.add('nds-drawer-open');

    // Tell the sticky header to treat the drawer as a modal so its
    // scroll-up "hide" behaviour is suspended while we're open.
    document.dispatchEvent(new CustomEvent('nds:modal:open', { detail: { source: 'drawer' } }));

    // Run the Motion-driven entry. Content inside the drawer stays static;
    // only the shell moves. Per-card stagger used to write inline transforms
    // to Fabric/Dyes and catalog cards, which caused the exact jump/flicker
    // seen on mobile when entering/leaving catalog screens.
    // No-op (returns null) when animEnabled is false — the CSS
    // transition handles the fade-in as a graceful fallback.
    if (animEnabled) {
      animateShellOpen();
    }

    // Focus management: land on the first focusable element inside
    // the drawer body (typically the first catalog tile) once the
    // entry motion has settled enough for the element to read as
    // "landed". 220ms with Motion (spring is ~95% done by then),
    // 320ms as a safe CSS-fallback buffer.
    setTimeout(function () {
      var first = $all(FOCUSABLE, drawer).filter(function (el) {
        return el.offsetParent !== null;
      })[0];
      if (first) try { first.focus({ preventScroll: true }); } catch (e) { first.focus(); }
    }, animEnabled ? 220 : 320);
  }

  function close(opts) {
    if (!drawer || !state.open) return;
    opts = opts || {};
    state.open = false;
    state.railHandoff = false;

    // Always collapse to main view on close so reopens are predictable.
    // Pass skipAnim — the drawer shell is about to fade out, so there's
    // no value in animating the rail back to main at the same time;
    // that would just read as a chaotic two-axis exit.
    setView('main', null, { skipAnim: true });
    closeSheet({ skipAnim: true });

    var burger = $('[data-burger-toggle]');
    if (burger) burger.setAttribute('aria-expanded', 'false');

    // Release the body lock. Safe in both direct and handoff paths:
    //   • Direct close  → body is fully unlocked.
    //   • Handoff close → the downstream modal has ALREADY set its
    //     own `body.style.overflow = 'hidden'` + `body.classList
    //     .add('nds-cm-open')` before dispatching nds:modal:open,
    //     so removing our class can't unlock the body — the modal's
    //     lock is still in effect. When the modal later closes it
    //     runs its own unlockBody() and everything is clean.
    // Because we never changed window.scrollY, there is nothing to
    // restore here — the viewport is already exactly where the user
    // left it.
    document.body.classList.remove('nds-drawer-open');

    // Notify the header unless we're handing off. On handoff the
    // downstream modal keeps the header pinned via its own
    // nds:modal:open → nds:modal:close cycle.
    if (!opts.silent) {
      document.dispatchEvent(new CustomEvent('nds:modal:close', { detail: { source: 'drawer' } }));
    }

    // Snap the ARIA + data flags to closed at the right moment.
    // Extracted so both the animated and non-animated paths share
    // the cleanup.
    var finalize = function () {
      drawer.setAttribute('data-open', 'false');
      drawer.setAttribute('aria-hidden', 'true');
      // Clear any lingering inline styles Motion left behind so
      // the CSS class rules own the resting state again.
      drawer.style.opacity = '';
      drawer.style.transform = '';
      $all('[data-nds-drawer-sub]', drawer).forEach(function (p) {
        p.style.transform = '';
        p.style.willChange = '';
        p.style.visibility = '';
        p.style.pointerEvents = '';
      });
    };

    if (animEnabled && !opts.handoff) {
      // Direct close — fade the shell out with a fast ease, then
      // flip the attributes. Keep data-open="true" during the
      // animation so CSS's visibility:hidden doesn't snap the
      // element invisible before the fade has run.
      animateShellClose().then(finalize);
    } else {
      // Handoff — the collection modal that's taking over will
      // mask the drawer as it rises; animating a fade would only
      // compete with the modal's own entrance. Snap closed.
      // Also: no-Motion fallback → let the CSS transition own
      // the fade via the data-open flip.
      stopAllAnims();
      finalize();
    }

    // Return focus where it came from (skip on handoff — the next
    // modal manages its own focus).
    if (!opts.handoff && state.lastFocus && typeof state.lastFocus.focus === 'function') {
      try { state.lastFocus.focus({ preventScroll: true }); } catch (e) { state.lastFocus.focus(); }
    }
  }

  function toggle() { state.open ? close() : open(); }

  // ──────────────────────────────────────────────────────────────
  //  Trigger wiring (delegated at document level so it survives
  //  section re-render; burger + close buttons).
  // ──────────────────────────────────────────────────────────────
  function bindTriggers() {
    // The site burger is the ONLY open/close control now. Click-delegation
    // at document level survives section re-renders (theme editor).
    document.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest('[data-burger-toggle], [data-nds-drawer-toggle], [data-nds-drawer-close]');
      if (!t) return;
      e.preventDefault();
      toggle();
    });
  }

  // ──────────────────────────────────────────────────────────────
  //  Views rail (main ↔ sub)
  // ──────────────────────────────────────────────────────────────
  function setView(view, cat, opts) {
    opts = opts || {};
    var prevView = state.view;
    var prevCat  = state.activeCat;
    var shouldAnimate = animEnabled && !opts.skipAnim;
    var keepPrevSubForBack = shouldAnimate && prevView === 'sub' && view === 'main' && !!prevCat;
    state.railHandoff = false;
    state.view = view;
    state.activeCat = cat || null;

    if (!rail) return;
    rail.setAttribute('data-view', view);

    // Activate the matching sub-view (if any) and deactivate the rest.
    // For backward rail (sub → main), keep the previous sub-view active until
    // the WAAPI slide completes. Otherwise CSS applies visibility:hidden before
    // the reverse rail can run, so the catalog cards disappear abruptly.
    $all('[data-nds-drawer-sub]', drawer).forEach(function (p) {
      var handle = p.getAttribute('data-nds-drawer-sub');
      var isActive = (view === 'sub' && handle === cat) ||
                     (keepPrevSubForBack && handle === prevCat);
      p.setAttribute('data-active', isActive ? 'true' : 'false');
      p.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    // Reset the active sub-panel scroll to top on entry, and sync
    // the sticky sub-header title to the category label the user
    // actually tapped (matches its main-view tile).
    if (view === 'sub') {
      var activePanel = $('[data-nds-drawer-sub="' + cat + '"]', drawer);
      if (activePanel) {
        activePanel.scrollTop = 0;
        var titleEl = $('[data-nds-drawer-sub-title]', activePanel);
        var trigger = $('[data-nds-drawer-open-cat="' + cat + '"]', drawer);
        if (titleEl && trigger) {
          titleEl.textContent = trigger.getAttribute('data-cat-title') || trigger.textContent.trim();
        }
      }
    }

    // Drive the rail slide with Motion unless the caller explicitly
    // opted out (close() does, because the drawer shell is about to
    // fade and a simultaneous rail-rewind would look chaotic). We
    // also skip when there's no actual view change — prevents a
    // redundant slide on programmatic setView calls.
    if (shouldAnimate) {
      if (prevView === 'main' && view === 'sub' && cat) {
        animateRail('forward', cat);
      } else if (prevView === 'sub' && view === 'main') {
        animateRail('backward', prevCat);
      }
    } else if (opts.skipAnim) {
      // Skip-anim path (drawer is collapsing) — any in-flight rail
      // slide must be killed AND its committed inline transform
      // cleared, otherwise the next open would see the rail stuck
      // mid-slide. Motion's stop() writes the current interpolated
      // value to inline via commitStyles(); we wipe it here so the
      // CSS resting rules own the transform again.
      stopAnim('rail');
      var mv = $('[data-nds-drawer-view="main"]', drawer);
      if (mv) mv.style.transform = '';
      $all('[data-nds-drawer-sub]', drawer).forEach(function (p) {
        p.style.transform = '';
        p.style.willChange = '';
        p.style.visibility = '';
        p.style.pointerEvents = '';
      });
    }
  }

  function bindCategoryOpens() {
    if (!drawer) return;
    $all('[data-nds-drawer-open-cat]', drawer).forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var cat = btn.getAttribute('data-nds-drawer-open-cat');
        if (!cat) return;
        setView('sub', cat);
      });
    });
  }

  function bindBackButtons() {
    btnBack.forEach(function (b) {
      b.addEventListener('click', function (e) {
        e.preventDefault();
        setView('main');
      });
    });
  }

  // ──────────────────────────────────────────────────────────────
  //  Localization bottom-sheet
  // ──────────────────────────────────────────────────────────────
  function bindAccordions() {
    if (!drawer) return;
    drawer.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-nds-drawer-accordion]');
      if (!btn || !drawer.contains(btn)) return;

      e.preventDefault();

      var item = btn.closest('.nds-drawer__link-item');
      var panelId = btn.getAttribute('aria-controls');
      var panel = panelId ? document.getElementById(panelId) : null;
      var list = item && item.closest('.nds-drawer__links');
      var willOpen = btn.getAttribute('aria-expanded') !== 'true';

      if (list) {
        $all('[data-nds-drawer-accordion]', list).forEach(function (other) {
          if (other === btn) return;
          var otherItem = other.closest('.nds-drawer__link-item');
          var otherPanelId = other.getAttribute('aria-controls');
          var otherPanel = otherPanelId ? document.getElementById(otherPanelId) : null;

          other.setAttribute('aria-expanded', 'false');
          if (otherItem) otherItem.setAttribute('data-expanded', 'false');
          if (otherPanel) otherPanel.setAttribute('aria-hidden', 'true');
        });
      }

      btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      if (item) item.setAttribute('data-expanded', willOpen ? 'true' : 'false');
      if (panel) panel.setAttribute('aria-hidden', willOpen ? 'false' : 'true');
    });
  }

  function openSheet() {
    if (!sheet) return;
    document.body.classList.add('nds-drawer-sheet-open');
    document.documentElement.classList.add('nds-drawer-sheet-open');

    // Prime off-screen state inline BEFORE flipping data-open so
    // the CSS [data-open="true"] transform:translateY(0) can't
    // snap-into-place instantly under motion-on (transition: none).
    if (animEnabled) {
      sheet.style.transform = 'translateY(100%)';
      if (sheetBackdrop) sheetBackdrop.style.opacity = '0';
    }

    sheet.setAttribute('data-open', 'true');
    if (sheetBackdrop) sheetBackdrop.setAttribute('data-open', 'true');

    if (animEnabled) animateSheetOpen();
  }

  function closeSheet(opts) {
    if (!sheet) return;
    opts = opts || {};

    // Drawer-level close() passes skipAnim:true — we're about to
    // fade the whole drawer shell, so the sheet should reset
    // instantly to avoid a second, competing exit animation.
    if (opts.skipAnim || !animEnabled) {
      stopAnim('sheet');
      stopAnim('backdrop');
      sheet.setAttribute('data-open', 'false');
      if (sheetBackdrop) sheetBackdrop.setAttribute('data-open', 'false');
      sheet.style.transform = '';
      if (sheetBackdrop) sheetBackdrop.style.opacity = '';
      document.body.classList.remove('nds-drawer-sheet-open');
      document.documentElement.classList.remove('nds-drawer-sheet-open');
      return;
    }

    // Keep data-open="true" during the slide so the element stays
    // visible for the fade. Flip to "false" on finish. This matches
    // how close() coordinates the drawer shell.
    animateSheetClose().then(function () {
      sheet.setAttribute('data-open', 'false');
      if (sheetBackdrop) sheetBackdrop.setAttribute('data-open', 'false');
      sheet.style.transform = '';
      if (sheetBackdrop) sheetBackdrop.style.opacity = '';
      document.body.classList.remove('nds-drawer-sheet-open');
      document.documentElement.classList.remove('nds-drawer-sheet-open');
    });
  }

  function bindSheet() {
    btnSheetToggle = $all('[data-nds-drawer-sheet-toggle]', drawer);
    btnSheetToggle.forEach(function (b) {
      b.addEventListener('click', function (e) {
        e.preventDefault();
        openSheet();
      });
    });
    // Wrap — closeSheet's opts parameter must not be the click
    // MouseEvent. opts is meant to be a plain options object
    // ({ skipAnim: true }); passing the event directly would
    // pollute opts.* with event fields and silently break any
    // future conditional we add.
    if (sheetClose)    sheetClose.addEventListener('click', function () { closeSheet(); });
    if (sheetBackdrop) sheetBackdrop.addEventListener('click', function () { closeSheet(); });

    // Currency conversion is handled by the NDS header script, which
    // listens to [data-currency-code]. The drawer only closes the sheet.
    $all('[data-nds-drawer-currency]', drawer).forEach(function (item) {
      item.addEventListener('click', function () {
        closeSheet();
      });
    });

    // Language change — write selection into the desktop form's
  }

  // ──────────────────────────────────────────────────────────────
  //  Keyboard — ESC closes sheet → then drawer, stepwise.
  // ──────────────────────────────────────────────────────────────
  function bindGlobalKeys() {
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (!state.open) return;
      if (sheet && sheet.getAttribute('data-open') === 'true') {
        closeSheet();
      } else if (state.view === 'sub') {
        setView('main');
      } else {
        close();
      }
    });
  }

  // ──────────────────────────────────────────────────────────────
  //  Collection-modal integration
  //  ─────────────────────────────
  //  nds-collection-modal.js captures the click on [data-nds-cm-open]
  //  at document-capture phase and calls stopPropagation() — meaning
  //  our own click listener would never fire. Instead, we listen for
  //  the `nds:modal:open` custom event which the collection modal
  //  dispatches AFTER it starts opening. When we see that event and
  //  the drawer is open, we do a "handoff close" — drawer closes
  //  without restoring body scroll (collection modal owns the lock
  //  now) and without firing nds:modal:close (header must stay in
  //  modal-open state for the next modal).
  // ──────────────────────────────────────────────────────────────
  function activeSubPanel(cat) {
    var handle = cat || state.activeCat;
    if (!handle || !drawer) return null;
    return $('[data-nds-drawer-sub="' + handle + '"]', drawer);
  }

  function railOutSubForHandoff(cat) {
    if (!drawer || !state.open || state.view !== 'sub') return;
    var sub = activeSubPanel(cat);
    if (!sub) return;

    state.railHandoff = true;
    stopAnim('rail');

    sub.setAttribute('data-active', 'true');
    sub.setAttribute('aria-hidden', 'false');
    sub.style.visibility = 'visible';
    sub.style.pointerEvents = 'none';
    sub.style.willChange = 'transform';

    if (!animEnabled) {
      sub.style.transform = 'translateX(-100%)';
      return;
    }

    if (!sub.style.transform) sub.style.transform = 'translateX(0%)';
    void sub.offsetWidth;
    var a = M.animate(
      sub,
      { transform: [null, 'translateX(-100%)'] },
      { easing: M.spring(SPRING_RAIL) }
    );
    anims.rail = a;
    a.finished.then(function () {
      if (anims.rail === a) anims.rail = null;
      sub.style.transform = 'translateX(-100%)';
    }).catch(function () {});
  }

  function railInSubForHandoff(cat) {
    if (!drawer || !state.open || state.view !== 'sub') return;
    var sub = activeSubPanel(cat);
    if (!sub) return;

    state.railHandoff = false;
    stopAnim('rail');

    sub.setAttribute('data-active', 'true');
    sub.setAttribute('aria-hidden', 'false');
    sub.style.visibility = 'visible';
    sub.style.pointerEvents = '';
    sub.style.willChange = 'transform';

    if (!animEnabled) {
      sub.style.transform = 'translateX(0%)';
      sub.style.visibility = '';
      return;
    }

    if (!sub.style.transform) sub.style.transform = 'translateX(-100%)';
    void sub.offsetWidth;
    var a = M.animate(
      sub,
      { transform: [null, 'translateX(0%)'] },
      { easing: M.spring(SPRING_RAIL) }
    );
    anims.rail = a;
    a.finished.then(function () {
      if (anims.rail === a) anims.rail = null;
      sub.style.transform = 'translateX(0%)';
      sub.style.visibility = '';
    }).catch(function () {});
  }

  function bindModalTriggers() {
    document.addEventListener('nds:modal:open', function (e) {
      if (!state.open) return;
      var detail = e.detail || {};
      // Our own open event has detail.source === 'drawer' — ignore.
      if (detail.source === 'drawer') return;
      if (detail.source === 'drawer-rail') {
        railOutSubForHandoff(state.activeCat);
        return;
      }
      // Any other modal opening → handoff close.
      close({ handoff: true, silent: true });
    });

    document.addEventListener('nds:modal:close', function (e) {
      if (!state.open) return;
      var detail = e.detail || {};
      if (detail.target !== 'drawer-rail') return;
      railInSubForHandoff(state.activeCat);
    });
  }

  // ──────────────────────────────────────────────────────────────
  //  Animation primitives (Motion One)
  //  ─────────────────────────────────
  //  All functions here are no-ops when `animEnabled === false`
  //  so callers can invoke them unconditionally and rely on the
  //  CSS transitions as a graceful fallback. Every animation is
  //  tracked in `anims` so interruptions (user taps burger
  //  mid-open, hits back while the rail is still sliding, etc.)
  //  stop the in-flight one instead of stacking.
  // ──────────────────────────────────────────────────────────────

  // Shell open — lift + fade + subtle scale.
  //
  // The starting state (inline `opacity: 0` + translated+scaled
  // transform) is expected to have been set by the caller IF this
  // is a fresh open. For a mid-close interruption, the caller
  // leaves the current inline values alone so Motion's spring
  // can read them and carry velocity into the reverse animation.
  // `[null, target]` keyframes make that possible: Motion treats
  // `null` as "read current" and passes it into the spring
  // generator along with the estimated velocity from any
  // previously-committed animation on the same property.
  function animateShellOpen() {
    if (!animEnabled) return null;
    // Don't stopAnim('shell') here — the caller (open()) already
    // called stopAllAnims() BEFORE deciding whether to seed the
    // initial state. Re-stopping would wipe the committed inline
    // values we just carefully preserved for the reverse case.
    anims.shell = M.animate(
      drawer,
      {
        opacity: [null, 1],
        transform: [null, 'translateY(0px) scale(1)']
      },
      { easing: M.spring(SPRING_SHELL) }
    );
    anims.shell.finished.then(function () {
      // Clear inline so subsequent setAttribute flips (and
      // underlying CSS classes) own the resting state again.
      drawer.style.opacity = '';
      drawer.style.transform = '';
    }).catch(function () {});
    return anims.shell;
  }

  // Shell close — fast snap-out. Returns a promise that resolves
  // (or rejects on interruption) when the fade finishes so the
  // caller can flip data-open="false" AFTER the opacity has
  // actually reached 0, avoiding a visible disappear-mid-fade.
  function animateShellClose() {
    if (!animEnabled) return Promise.resolve();
    stopAnim('shell');
    anims.shell = M.animate(
      drawer,
      {
        opacity: [null, 0],
        transform: [null, 'translateY(6px) scale(0.99)']
      },
      { duration: 0.22, easing: EASE_EXIT }
    );
    // .finished rejects on stop() — normalise to a no-throw
    // promise so callers can `.then(finalize)` safely.
    return anims.shell.finished.catch(function () {});
  }

  // Rail slide — forward (main → sub) or backward (sub → main).
  // The view layers move; catalog cards inside them stay static. This mirrors
  // the collection-modal rail handoff, which is the stable path in practice.
  function animateRail(direction, cat) {
    if (!animEnabled || !rail) return null;
    var main = $('[data-nds-drawer-view="main"]', drawer);
    var sub  = cat ? $('[data-nds-drawer-sub="' + cat + '"]', drawer) : null;
    if (!main || !sub) return null;

    stopAnim('rail');

    var forward = direction === 'forward';
    var mainFrom = forward ? 'translateX(0%)'   : 'translateX(-100%)';
    var mainTo   = forward ? 'translateX(-100%)': 'translateX(0%)';
    var subFrom  = forward ? 'translateX(100%)' : 'translateX(0%)';
    var subTo    = forward ? 'translateX(0%)'   : 'translateX(100%)';

    // Prime inline transforms so Motion has explicit start frames
    // and CSS's attribute-selector transforms can't snap first.
    main.style.transform = mainFrom;
    sub.style.transform  = subFrom;
    // eslint-disable-next-line no-unused-expressions
    void main.offsetWidth;

    var aMain = M.animate(
      main,
      { transform: [mainFrom, mainTo] },
      { easing: M.spring(SPRING_RAIL) }
    );
    var aSub = M.animate(
      sub,
      { transform: [subFrom, subTo] },
      { easing: M.spring(SPRING_RAIL) }
    );
    anims.rail = aSub;

    Promise.all([aMain.finished.catch(function () {}), aSub.finished.catch(function () {})])
      .then(function () {
        if (anims.rail === aSub) anims.rail = null;
        // Clear inline so the data-view / data-active attribute
        // rules in CSS own the resting transform again.
        main.style.transform = '';
        sub.style.transform = '';

        if (!forward && state.view === 'main' && state.activeCat === null) {
          sub.setAttribute('data-active', 'false');
          sub.setAttribute('aria-hidden', 'true');
          sub.style.visibility = '';
          sub.style.pointerEvents = '';
        }
      });

    return aSub;
  }

  // Sheet lift — translateY 100% → 0 with gentle spring, backdrop
  // fades in parallel.
  function animateSheetOpen() {
    if (!animEnabled || !sheet) return null;
    stopAnim('sheet');
    stopAnim('backdrop');

    sheet.style.transform = 'translateY(100%)';
    if (sheetBackdrop) sheetBackdrop.style.opacity = '0';
    // eslint-disable-next-line no-unused-expressions
    void sheet.offsetWidth;

    anims.sheet = M.animate(
      sheet,
      { transform: ['translateY(100%)', 'translateY(0%)'] },
      { easing: M.spring(SPRING_SHEET) }
    );
    if (sheetBackdrop) {
      anims.backdrop = M.animate(
        sheetBackdrop,
        { opacity: [0, 1] },
        { duration: 0.26, easing: [0.2, 0, 0, 1] }
      );
    }
    anims.sheet.finished.then(function () {
      sheet.style.transform = '';
    }).catch(function () {});
    if (anims.backdrop) {
      anims.backdrop.finished.then(function () {
        if (sheetBackdrop) sheetBackdrop.style.opacity = '';
      }).catch(function () {});
    }
    return anims.sheet;
  }

  // Sheet close — faster reverse. Returns a promise so the caller
  // can flip data-open="false" only once the slide finishes.
  function animateSheetClose() {
    if (!animEnabled || !sheet) return Promise.resolve();
    stopAnim('sheet');
    stopAnim('backdrop');

    anims.sheet = M.animate(
      sheet,
      { transform: [null, 'translateY(100%)'] },
      { duration: 0.22, easing: EASE_EXIT }
    );
    if (sheetBackdrop) {
      anims.backdrop = M.animate(
        sheetBackdrop,
        { opacity: [null, 0] },
        { duration: 0.22, easing: [0.4, 0, 1, 1] }
      );
    }
    var p = [anims.sheet.finished.catch(function () {})];
    if (anims.backdrop) p.push(anims.backdrop.finished.catch(function () {}));
    return Promise.all(p);
  }


  // ──────────────────────────────────────────────────────────────
  //  iOS scroll guard
  //  ────────────────
  //  On iOS Safari the `overscroll-behavior: contain` CSS helps but
  //  isn't always enough when the user flicks past the top/bottom of
  //  a scroll container: the page behind can still rubber-band. The
  //  only 100% reliable fix is to preventDefault() on touchmove when
  //  the active scroll container is at a boundary AND the user is
  //  trying to scroll PAST that boundary. We do that surgically so
  //  normal inner-scroll still works.
  // ──────────────────────────────────────────────────────────────
  function bindIosScrollGuard() {
    var startY = 0;
    document.addEventListener('touchstart', function (e) {
      if (!state.open || !e.touches || !e.touches[0]) return;
      startY = e.touches[0].clientY;
    }, { passive: true });

    document.addEventListener('touchmove', function (e) {
      if (!state.open || !e.touches || !e.touches[0]) return;

      // Find the nearest scrollable ancestor within the drawer.
      var el = e.target;
      var scroller = null;
      while (el && el !== document.body) {
        if (el === drawer) break;
        var style = window.getComputedStyle(el);
        var overflowY = style.overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
          scroller = el;
          break;
        }
        el = el.parentElement;
      }

      // If the touch target isn't inside a scrollable region within the
      // drawer, block the gesture entirely — it would otherwise bubble
      // up to <body> and rubber-band the page.
      if (!scroller) {
        e.preventDefault();
        return;
      }

      // Within a scroller: allow internal scroll, block only the
      // overscroll at the boundaries.
      var dy = e.touches[0].clientY - startY;
      var atTop = scroller.scrollTop <= 0;
      var atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
      if ((atTop && dy > 0) || (atBottom && dy < 0)) {
        e.preventDefault();
      }
    }, { passive: false });
  }

  // ──────────────────────────────────────────────────────────────
  //  Focus trap — keeps Tab inside the drawer when open.
  //  Lightweight, no external deps.
  // ──────────────────────────────────────────────────────────────
  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function bindFocusTrap() {
    document.addEventListener('keydown', function (e) {
      if (!state.open || e.key !== 'Tab') return;
      if (!drawer) return;

      var focusables = $all(FOCUSABLE, drawer).filter(function (el) {
        return el.offsetParent !== null;  // visible
      });
      if (focusables.length === 0) return;

      var first = focusables[0];
      var last  = focusables[focusables.length - 1];
      var active = document.activeElement;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        try { last.focus({ preventScroll: true }); } catch (err) { last.focus(); }
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        try { first.focus({ preventScroll: true }); } catch (err) { first.focus(); }
      }
    });
  }

  // ──────────────────────────────────────────────────────────────
  //  Bootstrap
  // ──────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on section reload (Shopify theme editor)
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-nds-drawer]')) {
      init();
    }
  });

  // Close on viewport resize to desktop — the drawer is a mobile-only UI
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      if (state.open && window.innerWidth >= 750) close();
    }, 150);
  });

  // Expose a tiny public API for debugging and future integrations
  window.NDSDrawer = {
    open: open,
    close: close,
    toggle: toggle,
    setView: setView,
    getState: function () { return Object.assign({}, state); }
  };

})();
